import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  styled,
  Paper,
  Fade,
  Slide,
  Zoom,
  Stack,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  alpha
} from '@mui/material';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BusinessIcon from '@mui/icons-material/Business';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { fetchLenders } from '../../services/userService';

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: 'transparent',
  padding: theme.spacing(3),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(4)
  }
}));

const ContentWrapper = styled(Container)(({ theme }) => ({
  maxWidth: '1400px !important',
  padding: 0,
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(0, 1)
  }
}));

const MainCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(20px)',
  borderRadius: '32px',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
  overflow: 'visible',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: '0 32px 80px rgba(0, 0, 0, 0.15)',
    transform: 'translateY(-4px)'
  }
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(6),
  '&:last-child': {
    paddingBottom: theme.spacing(6)
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(8),
    '&:last-child': {
      paddingBottom: theme.spacing(8)
    }
  }
}));

const SectionCard = styled(Paper)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  borderRadius: '24px',
  border: '1px solid rgba(255, 255, 255, 0.4)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'hidden',
  position: 'relative',
  '&:hover': {
    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
    '&::before': {
      opacity: 1,
      transform: 'scaleX(1)'
    }
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    opacity: 0,
    transform: 'scaleX(0)',
    transition: 'all 0.4s ease'
  }
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  padding: theme.spacing(4, 5),
  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
  borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
  '& .MuiSvgIcon-root': {
    color: '#667eea',
    fontSize: '1.5rem',
    padding: theme.spacing(1),
    borderRadius: '12px',
    background: 'rgba(102, 126, 234, 0.1)'
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '1.25rem',
  fontWeight: 700,
  margin: 0,
  letterSpacing: '-0.02em',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent'
}));

const SectionDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  marginTop: theme.spacing(1),
  lineHeight: 1.5,
  opacity: 0.8
}));

const ReadOnlyField = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderRadius: '16px',
  backgroundColor: 'rgba(248, 250, 255, 0.8)',
  border: '1px solid rgba(102, 126, 234, 0.1)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    backgroundColor: 'rgba(248, 250, 255, 1)',
    borderColor: 'rgba(102, 126, 234, 0.2)',
    boxShadow: '0 4px 16px rgba(102, 126, 234, 0.08)'
  }
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.75rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: theme.spacing(0.5)
}));

const FieldValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '0.875rem',
  fontWeight: 500,
  lineHeight: 1.4
}));

const ActionButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: '16px',
  padding: '14px 32px',
  fontSize: '0.875rem',
  fontWeight: 700,
  textTransform: 'none',
  letterSpacing: '0.02em',
  background: variant === 'contained'
    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    : 'transparent',
  border: variant === 'outlined' ? '2px solid rgba(102, 126, 234, 0.3)' : 'none',
  color: variant === 'contained' ? '#FFFFFF' : '#667eea',
  boxShadow: variant === 'contained' ? '0 8px 24px rgba(102, 126, 234, 0.3)' : 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: variant === 'contained' ? '0 12px 32px rgba(102, 126, 234, 0.4)' : '0 4px 16px rgba(102, 126, 234, 0.1)',
    background: variant === 'contained'
      ? 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)'
      : 'rgba(102, 126, 234, 0.05)'
  }
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  let color = "#4CAF50";
  let backgroundColor = "#E8F5E9";

  if (status === "new_proposal") {
    color = "#2196F3";
    backgroundColor = "#E3F2FD";
  } else if (status === "pending" || status === "in_progress") {
    color = "#FFA000";
    backgroundColor = "#FFF8E1";
  } else if (status === "rejected") {
    color = "#F44336";
    backgroundColor = "#FFEBEE";
  } else if (status === "accepted") {
    color = "#4CAF50";
    backgroundColor = "#E8F5E9";
  }

  return {
    color: color,
    backgroundColor: backgroundColor,
    fontWeight: 600,
    borderRadius: "16px",
  };
});

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (error) {
    console.error("Error formatting date:", error);
    return "N/A";
  }
};

const formatCurrency = (amount) => {
  if (!amount || amount === 0 || amount === "0") return "₹0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
};

const TableWrapper = styled(Box)(({ theme }) => ({
  '& .MuiTableContainer-root': {
    borderRadius: '16px',
    border: '1px solid rgba(229, 232, 235, 0.7)',
    boxShadow: '0 4px 24px -4px rgba(0, 0, 0, 0.04)',
    overflow: 'auto',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      boxShadow: '0 8px 32px -8px rgba(0, 0, 0, 0.06)'
    },
    '&::-webkit-scrollbar': {
      height: '8px',
      width: '8px'
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1',
      borderRadius: '4px'
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#888',
      borderRadius: '4px',
      '&:hover': {
        background: '#666'
      }
    }
  }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '16px',
  '&.MuiTableCell-head': {
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontSize: '13px',
    whiteSpace: 'nowrap',
    letterSpacing: '0.02em',
    borderBottom: '2px solid rgba(229, 232, 235, 0.7)'
  },
  '&.MuiTableCell-body': {
    color: theme.palette.text.primary,
    fontSize: '14px',
    letterSpacing: '0.01em',
    borderBottom: '1px solid rgba(229, 232, 235, 0.5)',
    '& .email': {
      color: theme.palette.primary.main,
      fontWeight: 500,
      textDecoration: 'none',
      transition: 'color 0.2s ease',
      '&:hover': {
        color: theme.palette.primary.dark
      }
    }
  }
}));

const EmptyState = styled(Box)(({ theme }) => ({
  padding: theme.spacing(6),
  textAlign: 'center',
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  borderRadius: '16px',
  border: '1px dashed rgba(0, 0, 0, 0.12)'
}));

const ReadOnlyLenderTable = ({ selectedLenderIds }) => {
  const [lenders, setLenders] = useState([]);
  const [filteredLenders, setFilteredLenders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    const fetchLendersData = async () => {
      try {
        setLoading(true);
        const response = await fetchLenders();
        
        const apiLenders = response?.result?.lenders || response?.lenders || [];
        const transformedLenders = apiLenders.map(lender => ({
          id: lender.id,
          lenderId: lender.lender_lenderId,
          name: lender.name,
          mobileNumber: lender.mobileNumber,
          email: lender.email,
          institutionType: lender.lender_financialInstitutionType,
          location: lender.lender_location || lender.address || '',
          lendingType: lender.lender_typeOfLending,
          retailLoans: lender.lender_typeOfRetailLoans,
          designation: lender.lender_designation,
          verified: lender.lender_verified
        })) || [];

        const selectedLenders = transformedLenders.filter(lender =>
          selectedLenderIds.includes(lender.lenderId)
        );

        setLenders(selectedLenders);
        setFilteredLenders(selectedLenders);
      } catch (error) {
        console.error('Error fetching lenders:', error);
      } finally {
        setLoading(false);
      }
    };

    if (selectedLenderIds && selectedLenderIds.length > 0) {
      fetchLendersData();
    }
  }, [selectedLenderIds]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (!selectedLenderIds || selectedLenderIds.length === 0) {
    return (
      <EmptyState>
        <Typography variant="h6" sx={{ color: 'text.primary', mb: 1 }}>
          No lenders selected
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No lenders have been selected for this proposal.
        </Typography>
      </EmptyState>
    );
  }

  return (
    <TableWrapper>
      <TableContainer component={Paper} elevation={0} sx={{ width: '100%' }}>
        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <CircularProgress size={32} />
            <Typography sx={{ mt: 2, color: 'text.secondary' }}>
              Loading selected lenders...
            </Typography>
          </Box>
        ) : filteredLenders.length === 0 ? (
          <EmptyState>
            <Typography variant="h6" sx={{ color: 'text.primary', mb: 1 }}>
              No lenders found
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              The selected lenders could not be found.
            </Typography>
          </EmptyState>
        ) : (
          <>
            <Table sx={{ minWidth: 1200 }}>
              <TableHead>
                <TableRow>
                  <StyledTableCell>User ID</StyledTableCell>
                  <StyledTableCell>Name</StyledTableCell>
                  <StyledTableCell>Mobile Number</StyledTableCell>
                  <StyledTableCell>Email</StyledTableCell>
                  <StyledTableCell>Institution Type</StyledTableCell>
                  <StyledTableCell>Location</StyledTableCell>
                  <StyledTableCell>Type of Lending</StyledTableCell>
                  <StyledTableCell>Retail Loans</StyledTableCell>
                  <StyledTableCell>Designation</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLenders
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((lender) => (
                    <TableRow
                      hover
                      key={lender.lenderId}
                      sx={{
                        '&:hover': {
                          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                        }
                      }}
                    >
                      <StyledTableCell>{lender.id}</StyledTableCell>
                      <StyledTableCell>{lender.name}</StyledTableCell>
                      <StyledTableCell>{lender.mobileNumber}</StyledTableCell>
                      <StyledTableCell>
                        <a href={`mailto:${lender.email}`} className="email">
                          {lender.email}
                        </a>
                      </StyledTableCell>
                      <StyledTableCell>{lender.institutionType}</StyledTableCell>
                      <StyledTableCell>{lender.location}</StyledTableCell>
                      <StyledTableCell>{lender.lendingType}</StyledTableCell>
                      <StyledTableCell>{lender.retailLoans}</StyledTableCell>
                      <StyledTableCell>{lender.designation}</StyledTableCell>
                      <StyledTableCell>
                        <Chip
                          label={lender.verified ? "Verified" : "Unverified"}
                          color={lender.verified ? "success" : "warning"}
                          size="small"
                          variant="outlined"
                        />
                      </StyledTableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>

            {filteredLenders.length > 0 && (
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredLenders.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: '1px solid rgba(229, 232, 235, 0.7)',
                  '.MuiTablePagination-select': {
                    borderRadius: '6px',
                  }
                }}
              />
            )}
          </>
        )}
      </TableContainer>
    </TableWrapper>
  );
};

const ProposalDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { proposalData, applicationFormData, otherOffers, applicationData } =
    location.state || {};

  const handleBack = () => {
    navigate(-1);
  };

  const handleDownloadDocument = (url, filename) => {
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (!location.state) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} sx={{ color: '#667eea' }} />
        </Box>
      </PageContainer>
    );
  }

  const isFromLoans = proposalData && !applicationFormData && !proposalData.applicationForm;

  if (!isFromLoans && !applicationFormData && !proposalData?.applicationForm) {
    return (
      <PageContainer>
        <ContentWrapper>
          <MainCard>
            <StyledCardContent>
              <Box sx={{ mb: 4 }}>
                <ActionButton
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBack}
                >
                  Back to Proposals
                </ActionButton>
              </Box>
              <Alert severity="error">No application data found</Alert>
            </StyledCardContent>
          </MainCard>
        </ContentWrapper>
      </PageContainer>
    );
  }

  if (isFromLoans) {
    return (
      <PageContainer>
        <ContentWrapper>
          <Box sx={{ mb: 4 }}>
            <ActionButton
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
            >
              Back to Loans
            </ActionButton>
          </Box>

          <MainCard>
            <StyledCardContent>
              <Stack spacing={4}>

                <Fade in timeout={300}>
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2
                  }}>
                    <Typography
                      variant="h3"
                      sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontWeight: 700,
                        fontSize: { xs: '1.75rem', md: '2.25rem' }
                      }}
                    >
                      Proposal Details
                    </Typography>
                    <StatusChip
                      label={proposalData.status?.replace(/_/g, " ").toUpperCase() || "N/A"}
                      status={proposalData.status}
                      size="medium"
                    />
                  </Box>
                </Fade>

                <Fade in timeout={400}>
                  <SectionCard>
                    <SectionHeader>
                      <AssignmentIcon />
                      <Box>
                        <SectionTitle>Proposal Information</SectionTitle>
                        <SectionDescription>
                          Basic details about the loan proposal
                        </SectionDescription>
                      </Box>
                    </SectionHeader>
                    <Box sx={{ p: 5 }}>
                      <Grid container spacing={4}>
                        <Grid item xs={12} md={4}>
                          <ReadOnlyField>
                            <FieldLabel>Proposal ID</FieldLabel>
                            <FieldValue>{proposalData.id || "N/A"}</FieldValue>
                          </ReadOnlyField>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <ReadOnlyField>
                            <FieldLabel>UUID</FieldLabel>
                            <FieldValue>{proposalData.uuid || "N/A"}</FieldValue>
                          </ReadOnlyField>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <ReadOnlyField>
                            <FieldLabel>Borrower ID</FieldLabel>
                            <FieldValue>{proposalData.borrowerId || "N/A"}</FieldValue>
                          </ReadOnlyField>
                        </Grid>
                      </Grid>
                    </Box>
                  </SectionCard>
                </Fade>

                <Slide direction="up" in timeout={500}>
                  <SectionCard>
                    <SectionHeader>
                      <DescriptionIcon />
                      <Box>
                        <SectionTitle>Timeline Information</SectionTitle>
                        <SectionDescription>
                          Important dates and timeline details
                        </SectionDescription>
                      </Box>
                    </SectionHeader>
                    <Box sx={{ p: 5 }}>
                      <Grid container spacing={4}>
                        <Grid item xs={12} md={4}>
                          <ReadOnlyField>
                            <FieldLabel>Created Date</FieldLabel>
                            <FieldValue>{formatDate(proposalData.createDatetime)}</FieldValue>
                          </ReadOnlyField>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <ReadOnlyField>
                            <FieldLabel>Commit Date</FieldLabel>
                            <FieldValue>{formatDate(proposalData.commitDatetime)}</FieldValue>
                          </ReadOnlyField>
                        </Grid>
                        {proposalData.deleteDatetime && (
                          <Grid item xs={12} md={4}>
                            <ReadOnlyField>
                              <FieldLabel>Delete Date</FieldLabel>
                              <FieldValue>{formatDate(proposalData.deleteDatetime)}</FieldValue>
                            </ReadOnlyField>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </SectionCard>
                </Slide>

                <Slide direction="up" in timeout={600}>
                  <SectionCard>
                    <SectionHeader>
                      <PersonIcon />
                      <Box>
                        <SectionTitle>Operator Information</SectionTitle>
                        <SectionDescription>
                          Details about who created and managed this proposal
                        </SectionDescription>
                      </Box>
                    </SectionHeader>
                    <Box sx={{ p: 5 }}>
                      <Grid container spacing={4}>
                        <Grid item xs={12} md={4}>
                          <ReadOnlyField>
                            <FieldLabel>Created By</FieldLabel>
                            <FieldValue>{proposalData.createOpName || "N/A"}</FieldValue>
                          </ReadOnlyField>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <ReadOnlyField>
                            <FieldLabel>Committed By</FieldLabel>
                            <FieldValue>{proposalData.commitOpName || "N/A"}</FieldValue>
                          </ReadOnlyField>
                        </Grid>
                        {proposalData.deleteOpName && (
                          <Grid item xs={12} md={4}>
                            <ReadOnlyField>
                              <FieldLabel>Deleted By</FieldLabel>
                              <FieldValue>{proposalData.deleteOpName}</FieldValue>
                            </ReadOnlyField>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </SectionCard>
                </Slide>

                <Slide direction="up" in timeout={700}>
                  <SectionCard>
                    <SectionHeader>
                      <DescriptionIcon />
                      <Box>
                        <SectionTitle>Documents</SectionTitle>
                        <SectionDescription>
                          Download available documents for this proposal
                        </SectionDescription>
                      </Box>
                    </SectionHeader>
                    <Box sx={{ p: 5 }}>
                      <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                          <ReadOnlyField>
                            <FieldLabel>CMA Document</FieldLabel>
                            {proposalData.cma ? (
                              <ActionButton
                                variant="outlined"
                                size="small"
                                startIcon={<FileDownloadIcon />}
                                onClick={() => handleDownloadDocument(
                                  proposalData.cma,
                                  `CMA_${proposalData.id || proposalData.uuid}.docx`
                                )}
                              >
                                Download CMA
                              </ActionButton>
                            ) : (
                              <FieldValue>Not Available</FieldValue>
                            )}
                          </ReadOnlyField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <ReadOnlyField>
                            <FieldLabel>One Pager</FieldLabel>
                            {proposalData.onePager ? (
                              <ActionButton
                                variant="outlined"
                                size="small"
                                startIcon={<FileDownloadIcon />}
                                onClick={() => handleDownloadDocument(
                                  proposalData.onePager,
                                  `OnePager_${proposalData.id || proposalData.uuid}.docx`
                                )}
                              >
                                Download One Pager
                              </ActionButton>
                            ) : (
                              <FieldValue>Not Available</FieldValue>
                            )}
                          </ReadOnlyField>
                        </Grid>
                      </Grid>
                    </Box>
                  </SectionCard>
                </Slide>

                {applicationData?.lenders && applicationData.lenders.length > 0 && (
                  <Slide direction="up" in timeout={800}>
                    <SectionCard>
                      <SectionHeader>
                        <AccountBalanceIcon />
                        <Box>
                          <SectionTitle>Selected Lenders</SectionTitle>
                          <SectionDescription>
                            Lenders selected for this proposal
                          </SectionDescription>
                        </Box>
                      </SectionHeader>
                      <Box sx={{ p: 5 }}>
                        <ReadOnlyLenderTable selectedLenderIds={applicationData.lenders} />
                      </Box>
                    </SectionCard>
                  </Slide>
                )}

                {proposalData.ip && (
                  <Slide direction="up" in timeout={900}>
                    <SectionCard>
                      <SectionHeader>
                        <LocationOnIcon />
                        <Box>
                          <SectionTitle>Location Information</SectionTitle>
                          <SectionDescription>
                            Geographic and network details
                          </SectionDescription>
                        </Box>
                      </SectionHeader>
                      <Box sx={{ p: 5 }}>
                        <Grid container spacing={4}>
                          <Grid item xs={12} md={4}>
                            <ReadOnlyField>
                              <FieldLabel>IP Address</FieldLabel>
                              <FieldValue>{proposalData.ip}</FieldValue>
                            </ReadOnlyField>
                          </Grid>
                          {proposalData.latitude && (
                            <Grid item xs={12} md={4}>
                              <ReadOnlyField>
                                <FieldLabel>Latitude</FieldLabel>
                                <FieldValue>{proposalData.latitude}</FieldValue>
                              </ReadOnlyField>
                            </Grid>
                          )}
                          {proposalData.longitude && (
                            <Grid item xs={12} md={4}>
                              <ReadOnlyField>
                                <FieldLabel>Longitude</FieldLabel>
                                <FieldValue>{proposalData.longitude}</FieldValue>
                              </ReadOnlyField>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </SectionCard>
                  </Slide>
                )}

              </Stack>
            </StyledCardContent>
          </MainCard>
        </ContentWrapper>
      </PageContainer>
    );
  }

  const formData = applicationFormData || proposalData?.applicationForm;

  return (
    <PageContainer>
      <ContentWrapper>
        <Box sx={{ mb: 4 }}>
          <ActionButton
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back to Proposals
          </ActionButton>
        </Box>

        <MainCard>
          <StyledCardContent>
            <Stack spacing={4}>

              {/* Header Section */}
              <Fade in timeout={300}>
                <Typography
                  variant="h3"
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 700,
                    fontSize: { xs: '1.75rem', md: '2.25rem' }
                  }}
                >
                  Application Details
                </Typography>
              </Fade>

              {otherOffers && (
                <Fade in timeout={400}>
                  <Alert severity="info">
                    Other Offers: {otherOffers}
                  </Alert>
                </Fade>
              )}

              <Fade in timeout={500}>
                <SectionCard>
                  <SectionHeader>
                    <AssignmentIcon />
                    <Box>
                      <SectionTitle>Basic Information</SectionTitle>
                      <SectionDescription>
                        Core details about the loan application
                      </SectionDescription>
                    </Box>
                  </SectionHeader>
                  <Box sx={{ p: 5 }}>
                    <Grid container spacing={4}>
                      <Grid item xs={12} md={4}>
                        <ReadOnlyField>
                          <FieldLabel>Application Number</FieldLabel>
                          <FieldValue>{formData.applicationNumber || "N/A"}</FieldValue>
                        </ReadOnlyField>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <ReadOnlyField>
                          <FieldLabel>Borrower Type</FieldLabel>
                          <FieldValue sx={{ textTransform: "capitalize" }}>
                            {formData.borrowerType?.replace(/_/g, " ") || "N/A"}
                          </FieldValue>
                        </ReadOnlyField>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <ReadOnlyField>
                          <FieldLabel>Entity Type</FieldLabel>
                          <FieldValue sx={{ textTransform: "capitalize" }}>
                            {formData.entityType || "N/A"}
                          </FieldValue>
                        </ReadOnlyField>
                      </Grid>
                    </Grid>
                  </Box>
                </SectionCard>
              </Fade>

              <Slide direction="up" in timeout={600}>
                <SectionCard>
                  <SectionHeader>
                    <BusinessIcon />
                    <Box>
                      <SectionTitle>Company Information</SectionTitle>
                      <SectionDescription>
                        Business and company details
                      </SectionDescription>
                    </Box>
                  </SectionHeader>
                  <Box sx={{ p: 5 }}>
                    <Grid container spacing={4}>
                      <Grid item xs={12} md={4}>
                        <ReadOnlyField>
                          <FieldLabel>Name as per GST</FieldLabel>
                          <FieldValue>{formData.nameAsPerGST || "N/A"}</FieldValue>
                        </ReadOnlyField>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <ReadOnlyField>
                          <FieldLabel>GSTIN</FieldLabel>
                          <FieldValue>{formData.gstin || "N/A"}</FieldValue>
                        </ReadOnlyField>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <ReadOnlyField>
                          <FieldLabel>Company CIN</FieldLabel>
                          <FieldValue>{formData.companyCin || "N/A"}</FieldValue>
                        </ReadOnlyField>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <ReadOnlyField>
                          <FieldLabel>Udyam Registration Number</FieldLabel>
                          <FieldValue>{formData.udyamRegistrationNo || "N/A"}</FieldValue>
                        </ReadOnlyField>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <ReadOnlyField>
                          <FieldLabel>PAN Number</FieldLabel>
                          <FieldValue>{formData.panNumber || "N/A"}</FieldValue>
                        </ReadOnlyField>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <ReadOnlyField>
                          <FieldLabel>Total Sales (Current Year)</FieldLabel>
                          <FieldValue>
                            {formData.totalSalesCurrentYear
                              ? formatCurrency(formData.totalSalesCurrentYear)
                              : "N/A"}
                          </FieldValue>
                        </ReadOnlyField>
                      </Grid>
                    </Grid>
                  </Box>
                </SectionCard>
              </Slide>

              <Slide direction="up" in timeout={700}>
                <SectionCard>
                  <SectionHeader>
                    <PersonIcon />
                    <Box>
                      <SectionTitle>Contact Information</SectionTitle>
                      <SectionDescription>
                        Contact details and address information
                      </SectionDescription>
                    </Box>
                  </SectionHeader>
                  <Box sx={{ p: 5 }}>
                    <Grid container spacing={4}>
                      <Grid item xs={12} md={4}>
                        <ReadOnlyField>
                          <FieldLabel>Email</FieldLabel>
                          <FieldValue>{formData.email || "N/A"}</FieldValue>
                        </ReadOnlyField>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <ReadOnlyField>
                          <FieldLabel>Mobile Number</FieldLabel>
                          <FieldValue>{formData.mobileNo || "N/A"}</FieldValue>
                        </ReadOnlyField>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <ReadOnlyField>
                          <FieldLabel>Address</FieldLabel>
                          <FieldValue>{formData.addr || "N/A"}</FieldValue>
                        </ReadOnlyField>
                      </Grid>
                    </Grid>
                  </Box>
                </SectionCard>
              </Slide>

              <Slide direction="up" in timeout={800}>
                <SectionCard>
                  <SectionHeader>
                    <AccountBalanceIcon />
                    <Box>
                      <SectionTitle>Loan Requirements</SectionTitle>
                      <SectionDescription>
                        Loan amount and purpose details
                      </SectionDescription>
                    </Box>
                  </SectionHeader>
                  <Box sx={{ p: 5 }}>
                    <Grid container spacing={4}>
                      <Grid item xs={12} md={4}>
                        <ReadOnlyField>
                          <FieldLabel>Fund Requirement</FieldLabel>
                          <FieldValue>{formData.fundRequirement || "N/A"}</FieldValue>
                        </ReadOnlyField>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <ReadOnlyField>
                          <FieldLabel>Security Offered</FieldLabel>
                          <FieldValue sx={{ textTransform: "capitalize" }}>
                            {formData.securityOffered || "N/A"}
                          </FieldValue>
                        </ReadOnlyField>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <ReadOnlyField>
                          <FieldLabel>End Use of Fund</FieldLabel>
                          <FieldValue>{formData.endUseOfFund || "N/A"}</FieldValue>
                        </ReadOnlyField>
                      </Grid>
                    </Grid>
                  </Box>
                </SectionCard>
              </Slide>

              <Slide direction="up" in timeout={900}>
                <SectionCard>
                  <SectionHeader>
                    <DescriptionIcon />
                    <Box>
                      <SectionTitle>Additional Information</SectionTitle>
                      <SectionDescription>
                        Detailed descriptions and additional details
                      </SectionDescription>
                    </Box>
                  </SectionHeader>
                  <Box sx={{ p: 5 }}>
                    <Grid container spacing={4}>
                      <Grid item xs={12} md={6}>
                        <ReadOnlyField>
                          <FieldLabel>Description of Security</FieldLabel>
                          <FieldValue>{formData.descriptionOfSecurity || "N/A"}</FieldValue>
                        </ReadOnlyField>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <ReadOnlyField>
                          <FieldLabel>Description of Business</FieldLabel>
                          <FieldValue>{formData.descriptionOfBusiness || "N/A"}</FieldValue>
                        </ReadOnlyField>
                      </Grid>
                    </Grid>
                  </Box>
                </SectionCard>
              </Slide>

            </Stack>
          </StyledCardContent>
        </MainCard>
      </ContentWrapper>
    </PageContainer>
  );
};

export default ProposalDetails;
